import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × DELACROIX: Vibrant control — structured emotion
// Vivid diagonals, chromatic energy, controlled chaos
export function VasarelyDelacroixMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Dynamic color field */}
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />

      <g transform="translate(397 540)">
        {/* Vivid diagonal energy */}
        {Array.from({ length: 20 }).map((_, i) => {
          const angle = -60 + i * 6;
          const rad = (angle * Math.PI) / 180;
          const length = 600;
          const offset = (i - 10) * 30;
          
          return (
            <line
              key={i}
              x1={Math.cos(rad) * -300 + offset}
              y1={Math.sin(rad) * -300}
              x2={Math.cos(rad) * 300 + offset}
              y2={Math.sin(rad) * 300}
              stroke={i % 3 === 0 ? brand.amber : i % 3 === 1 ? brand.aqua : brand.violet}
              strokeWidth="12"
              opacity={0.3}
            />
          );
        })}

        {/* Chromatic vortex */}
        {Array.from({ length: 30 }).map((_, i) => {
          const angle = i * 0.6;
          const r = 40 + i * 9;
          const x = Math.cos(angle) * 15;
          const y = Math.sin(angle) * 15;
          const colors = [brand.amber, brand.aqua, brand.violet];
          
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={r}
              fill="none"
              stroke={colors[i % 3]}
              strokeWidth="4"
              opacity={0.4 - i * 0.01}
            />
          );
        })}

        {/* Dynamic color blocks (controlled chaos) */}
        {Array.from({ length: 16 }).map((_, i) =>
          Array.from({ length: 16 }).map((_, j) => {
            const x = (i - 7.5) * 38;
            const y = (j - 7.5) * 38;
            const energy = Math.sin(i * 0.5) * Math.cos(j * 0.5);
            const rotation = energy * 30;
            const colors = [brand.amber, brand.aqua, brand.violet];
            const colorIndex = Math.floor((i + j) % 3);
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 15}
                y={y - 15}
                width="30"
                height="30"
                fill={colors[colorIndex]}
                opacity={0.25 + Math.abs(energy) * 0.2}
                transform={`rotate(${rotation} ${x} ${y})`}
              />
            );
          })
        )}

        {/* Expressive brush strokes (optical) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * Math.PI) / 6;
          const r1 = 120;
          const r2 = 240;
          const width = 20 + Math.sin(i) * 10;
          
          return (
            <line
              key={i}
              x1={Math.cos(angle) * r1}
              y1={Math.sin(angle) * r1}
              x2={Math.cos(angle) * r2}
              y2={Math.sin(angle) * r2}
              stroke={i % 2 === 0 ? brand.amber : brand.violet}
              strokeWidth={width}
              opacity="0.3"
              strokeLinecap="round"
            />
          );
        })}

        {/* Passionate geometry nodes */}
        {[
          { x: -160, y: -120, r: 45 },
          { x: 170, y: -100, r: 50 },
          { x: -140, y: 130, r: 48 },
          { x: 150, y: 140, r: 42 },
          { x: 0, y: 0, r: 60 },
        ].map((node, i) => (
          <g key={i}>
            <circle cx={node.x} cy={node.y} r={node.r} fill={brand.amber} opacity="0.3" />
            <circle cx={node.x} cy={node.y} r={node.r * 0.7} fill={brand.aqua} opacity="0.25" />
            <circle cx={node.x} cy={node.y} r={node.r * 0.4} fill={brand.violet} opacity="0.3" />
          </g>
        ))}

        {/* Rebellion structure (controlled tension) */}
        {[-1, 1].map((dir) => (
          <line
            key={dir}
            x1={dir * -280}
            y1="-280"
            x2={dir * 280}
            y2="280"
            stroke={brand.amber}
            strokeWidth="4"
            opacity="0.3"
            strokeDasharray="20 10"
          />
        ))}
      </g>

      {/* Romantic energy label */}
      <text x="397" y="1030" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.amber} opacity="0.6" fontStyle="italic">
        énergie romantique
      </text>
    </g>
  );
}
